import { useEffect, useState } from 'react';

import { RoutingContext } from '@repo/types-client';

type Callback<T = unknown> = (context: RoutingContext<T>) => void;

const subscriptions: Callback[] = [];

const _context: { current: RoutingContext | undefined } = {
  current: undefined,
};

/**
 * Notifies all subscribers that the routing context has changed.
 * @function notifyRoutingContextChanged
 * @param {RoutingContext} context - The new routing context.
 */
const notifyRoutingContextChanged = (context: RoutingContext) => {
  _context.current = context;
  subscriptions.forEach((subscription) => subscription(context));
};

/**
 * Subscribes to routing context changes.
 * @function subscribe
 * @template T - The type of the routing context.
 * @param {Callback<T>} callback - The callback function to be called when the routing context changes.
 * @returns {object} An object with an unsubscribe method to remove the subscription.
 */
const subscribe = <T = unknown>(callback: Callback<T>) => {
  subscriptions.push(callback as Callback);
  // if (_context.current) callback(<RoutingContext<T>>_context.current);
  return {
    unsubscribe: () => {
      const index = subscriptions.indexOf(callback as Callback);
      if (index < 0) return;
      subscriptions.splice(index, 1);
    },
  };
};

/**
 * Custom hook to use the routing context.
 * @function useRoutingContext
 * @returns {object} An object containing the current routing context and a subscribe method.
 */
const useRoutingContext = () => {
  const [context, setContext] = useState(_context.current);

  useEffect(() => {
    const subscription = subscribe(setContext);
    return () => subscription.unsubscribe();
  }, []);

  return {
    routingContext: context,
    subscribe,
  };
};

export { useRoutingContext, notifyRoutingContextChanged };
